/**
 * Sistema de breakpoints + utilidades responsive del PMIS.
 *
 * Filosofía pragmática:
 *   - 4 breakpoints, no más.
 *   - `xs` = celular vertical (< 640px).
 *   - `sm` = tablet vertical o celular grande horizontal (640–1023px).
 *   - `md` = tablet horizontal / laptop chico (1024–1279px).
 *   - `lg` = desktop completo (≥ 1280px).
 *
 * Reglas de uso en el PMIS:
 *   - Pantallas operativas pesadas (cotización SO, AFE, Budget) son
 *     **md+ obligatorio**. En `xs/sm` mostramos un mensaje "Esta pantalla
 *     requiere tablet horizontal o laptop".
 *   - Listados (PR, PO, Actas, RDs) usan `ResponsiveTable`: tabla en
 *     `md+`, cards apiladas en `xs/sm`.
 *   - Home y Panel de Alertas son responsive nativos — ya hechos en
 *     CSS Grid.
 *   - RD del día tiene UI especial en `xs` (cámara nativa, ínputs grandes).
 */

import { useEffect, useState } from "react";

export type Breakpoint = "xs" | "sm" | "md" | "lg";

export const BREAKPOINTS = {
  xs: 0, // < 640
  sm: 640, // tablet vertical
  md: 1024, // tablet horizontal / laptop chico
  lg: 1280, // desktop
} as const;

/**
 * Touch targets recomendados por Apple HIG / Material Design.
 * Aplicamos 44px en `xs/sm` para botones, 36px en `md+` (mouse).
 */
export const TOUCH_TARGET = {
  mobile: 44,
  desktop: 36,
} as const;

/** Espaciado por densidad. Se aprieta en `md+` y se afloja en `xs/sm`. */
export const SPACING = {
  mobile: {
    pageGutter: 12,
    cardGap: 12,
    inputHeight: 44,
    fontBase: 14,
  },
  desktop: {
    pageGutter: 24,
    cardGap: 16,
    inputHeight: 32,
    fontBase: 13,
  },
} as const;

/**
 * Devuelve el breakpoint actual del viewport. SSR-safe: durante el primer
 * render asume "md" (la mayoría de los usuarios), después se ajusta al
 * tamaño real en el effect.
 */
export function useBreakpoint(): Breakpoint {
  const [bp, setBp] = useState<Breakpoint>(() => {
    if (typeof window === "undefined") return "md";
    return calcular(window.innerWidth);
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setBp(calcular(window.innerWidth));
    onResize(); // sync inicial post-mount
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return bp;
}

/** True si el breakpoint actual es `xs` o `sm` (celular o tablet vertical). */
export function useIsMobile(): boolean {
  const bp = useBreakpoint();
  return bp === "xs" || bp === "sm";
}

/** True si es exactamente `xs` (celular). */
export function useIsPhone(): boolean {
  return useBreakpoint() === "xs";
}

function calcular(width: number): Breakpoint {
  if (width >= BREAKPOINTS.lg) return "lg";
  if (width >= BREAKPOINTS.md) return "md";
  if (width >= BREAKPOINTS.sm) return "sm";
  return "xs";
}

/** Comparador: true si `actual` es ≥ `min` en el orden xs<sm<md<lg. */
export function bpAtLeast(actual: Breakpoint, min: Breakpoint): boolean {
  const orden: Breakpoint[] = ["xs", "sm", "md", "lg"];
  return orden.indexOf(actual) >= orden.indexOf(min);
}
